import { useContext } from "react";
import { Link } from "react-router-dom";

import {
  confirmedAdd,
  offerImageUrl,
  offerPdpPath,
} from "@/components/chat/ChatCommerceCard";
import ChatProductCard from "@/components/chat/ChatProductCard";
import {
  PLAN_UNAVAILABLE,
  programPdpPath,
} from "@/components/chat/ChatProgramCard";
import ChatViewCart from "@/components/chat/ChatViewCart";
import { CartContext } from "@/contexts/cartContext";
import { hasOfferLine } from "@/services/commerceAddOutcome";
import type { SavedOffer } from "@/services/commerceOffers";
import type { SavedProgramOffer } from "@/services/commerceProgramOffers";

/**
 * A card restored from history: no price or add, since the saved offer may be stale.
 * withdrawn: a refresh answered without its offer (unavailable, or already in the cart).
 * inCart: the refresh's in-cart answer; undefined falls back to this page's confirmed add.
 * A restored program shows only its title: no price or product list. One no longer
 * available is a line of chat text, as on the live card.
 */
export default function ChatSavedCommerceCard({
  offer,
  withdrawn = false,
  inCart: serverInCart,
}: {
  offer: SavedOffer | SavedProgramOffer;
  withdrawn?: boolean;
  inCart?: boolean;
}) {
  const { cart } = useContext(CartContext);
  // Only a saved program keeps its kind.
  const isProgram = "kind" in offer && offer.kind === "program";
  const pdpPath = isProgram ? programPdpPath(offer) : offerPdpPath(offer);
  const added = confirmedAdd(offer.offer_id);
  const inCart =
    serverInCart ?? (!!added && !!cart && hasOfferLine(added, cart));
  if (isProgram && withdrawn && !inCart) return PLAN_UNAVAILABLE;
  return (
    <ChatProductCard
      name={offer.name}
      description={isProgram ? "" : offer.description}
      image_url={offerImageUrl(offer)}
    >
      {withdrawn && (
        <p role="status" className="text-[var(--chat-text-xs)] leading-[1.5]">
          {inCart ? "In your cart" : "No longer available."}
        </p>
      )}
      {withdrawn && inCart && <ChatViewCart />}
      {!withdrawn && pdpPath && (
        <Link
          to={pdpPath}
          aria-label={`View details for ${offer.name}`}
          className="text-[13px] font-semibold text-[var(--chat-black)] underline"
        >
          View details
        </Link>
      )}
    </ChatProductCard>
  );
}
